import { useSetAtom, useAtomValue } from "jotai";
import { Node, NodeProps } from "@xyflow/react";
import { Button, ColorName, Colors, Text } from "@zapier/design-system";
import { IconName } from "@zapier/zinnia-icons";
import {
  DefaultNodeData,
  determineAssetType,
  getAssetId,
  NodeType,
  unlinkAssetsAtomFamily,
  capitalizedAssetTypeDisplayAtom,
  ZapNodeData,
} from "@zapier/canvas-state";
import { StepTitle } from "../../../../StepTitle";
import { AssetPreview } from "../AssetPreview";
import { AssetIcon } from "../AssetIcon";
import styles from "./AssetNotFound.module.css";

type Props = {
  placeholder?: string;
  iconName?: IconName;
};

type AssetNotFoundProps = Pick<
  NodeProps<Node<DefaultNodeData | ZapNodeData>>,
  "id" | "data" | "type" | "selected"
> &
  Props;

export function AssetNotFound(props: AssetNotFoundProps) {
  const { id, data, type, selected, iconName, placeholder } = props;
  const getAssetDisplayName = useAtomValue(capitalizedAssetTypeDisplayAtom);

  const assetType = determineAssetType(
    (data as DefaultNodeData).services,
    type as NodeType,
  );
  const assetId = getAssetId(data, assetType);
  const isBlankAgentNode = assetType === "agent" && !assetId;

  const unlinkAssets = useSetAtom(unlinkAssetsAtomFamily(id));

  return (
    <div className={styles.container}>
      <div className={styles.bodyContainer}>
        <div>
          <div className={styles.body}>
            <div className={styles.previewImage}>
              {assetType && assetType !== "agent" && (
                <AssetPreview
                  id={id}
                  assetType={assetType}
                  assetId={assetId}
                  showAssetDetailsButton={false}
                  projectId={(data as DefaultNodeData).projectId}
                  isAssetNotFound={true}
                />
              )}
            </div>
            <StepTitle
              nodeId={id}
              label={data.label}
              isSelected={Boolean(selected)}
              placeholder={placeholder}
              canNodeGetAiSuggestion={false}
              rows={2}
              isViewOnlyMode={true}
              isBlankAgentNode={isBlankAgentNode}
              shouldHaveCharLimit={assetType !== "agent"}
            />
          </div>
        </div>
      </div>
      <div className={styles.footer}>
        {iconName && <AssetIcon iconName={iconName} color="GrayWarm6" />}
        {assetType && (
          <Text type="MinimalPrint1" color={Colors.GrayWarm3 as ColorName}>
            {getAssetDisplayName(assetType)} not found
          </Text>
        )}
        <Button onPress={unlinkAssets} variant="tertiary" size="compact">
          Unlink
        </Button>
      </div>
    </div>
  );
}
